<script lang="ts">
  import { page } from '$app/state';
  import type { DrawerNavigationContext } from '$lib/componentTypes';
  import { onMount, setContext } from 'svelte';

  let { children } = $props();

  const activePage = $derived(page.route.id?.split('/')[4]);
  const adminsActive = $derived(activePage === 'admins');
  const instructorsActive = $derived(activePage === 'instructors');
  const studentsActive = $derived(activePage === 'students');

  let isDrawerOpen = $state(false);

  function openDrawer() {
    isDrawerOpen = true;
  }

  function closeDrawer() {
    isDrawerOpen = false;
  }

  setContext<DrawerNavigationContext>('users-drawer', {
    openDrawer,
    closeDrawer,
  });

  const mediaMatcher = window.matchMedia('(width >= 768px)');
  onMount(() => {
    const mediaMatcherChangeHandler = () => {
      closeDrawer();
    };

    mediaMatcher.addEventListener('change', mediaMatcherChangeHandler);

    return () => {
      mediaMatcher.removeEventListener('change', mediaMatcherChangeHandler);
    };
  });
</script>

<div class="users-layout">
  <div class="with-sidebar">
    <div class="drawer" class:open={isDrawerOpen} role="dialog" id="usersDrawer" aria-labelledby="usersDrawerTitle">
      <div class="drawer-header offcanvas-header px-3 py-2">
        <h3 class="offcanvas-title" id="usersDrawerTitle">Users</h3>
        <button type="button" class="btn-close" aria-label="Close" onclick={closeDrawer}></button>
      </div>
      <nav class="nav flex-column">
        <a
          class={['nav-link', adminsActive && 'active']}
          aria-current={adminsActive ? 'page' : null}
          onclick={closeDrawer}
          href="/admin/users/admins">Admins</a
        >
        <a
          class={['nav-link', instructorsActive && 'active']}
          aria-current={instructorsActive ? 'page' : null}
          onclick={closeDrawer}
          href="/admin/users/instructors">Instructors</a
        >
        <a
          class={['nav-link', studentsActive && 'active']}
          aria-current={studentsActive ? 'page' : null}
          onclick={closeDrawer}
          href="/admin/users/students">Students</a
        >
      </nav>
    </div>
    <div>
      <main class="container p-4" id="main-content">
        {@render children?.()}
      </main>
    </div>
  </div>
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="backdrop" class:open={isDrawerOpen} onclick={closeDrawer}></div>
</div>

<style>
  .users-layout {
    --transition-duration: 0.3s;
    --transition-ease: ease-in-out;
    position: relative;
    flex-grow: 1; /* puts footer on the bottom of the screen if there's not enough to fill screen */
  }

  .with-sidebar {
    display: grid;
    grid-template-columns: 1fr;
  }

  .nav-link {
    --bs-nav-link-font-size: 1rem;
  }

  .nav-link.active {
    background-color: #f8f7f7;
  }

  .nav-link:hover {
    background-color: var(--uams-primary-red);
    color: white;
  }

  .backdrop {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: 0;
    pointer-events: none;

    transition: opacity var(--transition-duration) var(--transition-ease);
  }

  .backdrop.open {
    pointer-events: auto;
    opacity: 1;
  }

  .drawer {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    background-color: white;
    min-width: 200px;
    z-index: 1000;

    transform: translateX(-200px);
    transition: transform var(--transition-duration) var(--transition-ease);
  }

  .drawer.open {
    transform: translateX(0);
  }

  .drawer-header {
    border-bottom: 2px solid var(--bs-secondary-bg);
  }

  @media screen and (min-width: 768px) {
    .with-sidebar {
      grid-template-columns: 200px 1fr;
    }

    .backdrop {
      display: none;
    }

    .drawer {
      transform: none;
      position: unset;
      border-right: 2px solid var(--bs-secondary-bg);
      transition: none;
    }

    .drawer-header {
      display: none;
    }
  }
</style>
